import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ScanBarcode, Send } from "lucide-react";

const ACTIONS = ["INFO", "RECEIVE", "PICK", "MOVE", "COUNT"];

export default function Barcode() {
  const [warehouses, setWarehouses] = useState([]);
  const [scans, setScans] = useState([]);
  const [form, setForm] = useState({ barcode: "", action: "INFO", warehouse_id: "", to_warehouse_id: "", quantity: 1 });
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    const [w, s] = await Promise.all([api.get("/inventory/warehouses"), api.get("/warehouse/scans")]);
    setWarehouses(w.data); setScans(s.data);
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    if (!form.barcode) { toast.error("Enter or scan a barcode"); return; }
    try {
      const { data } = await api.post("/warehouse/barcode/scan", form);
      setLastResult(data);
      toast.success(`${data.action} → ${data.resolved.kind.toUpperCase()} ${data.resolved.sku || data.resolved.code || data.resolved.number || data.resolved.id}`);
      setForm({ ...form, barcode: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
      setLastResult(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-barcode">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
          <ScanBarcode className="w-7 h-7 text-primary" /> Barcode Scanning
        </h1>
        <p className="text-muted-foreground">
          Scan SKUs, bins, packs or equipment. Stock-changing actions (RECEIVE / PICK / MOVE) post real movements.
        </p>
      </div>

      <div className="surface-card p-6 space-y-4 max-w-3xl">
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-5">
            <Label>Barcode</Label>
            <Input
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && scan()}
              data-testid="bc-input"
              placeholder="Scan or type code (SKU / bin / pack / equipment)…"
              autoFocus
            />
          </div>
          <div className="col-span-2">
            <Label>Action</Label>
            <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
              <SelectTrigger data-testid="bc-action"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Qty</Label>
            <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value || "1") })} data-testid="bc-qty" />
          </div>
          <div className="col-span-3">
            <Button className="w-full" onClick={scan} data-testid="bc-scan"><Send className="w-4 h-4 mr-1" /> Scan</Button>
          </div>

          {(form.action === "RECEIVE" || form.action === "PICK" || form.action === "MOVE" || form.action === "COUNT") && (
            <>
              <div className="col-span-6">
                <Label>Warehouse</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                  <SelectTrigger data-testid="bc-wh"><SelectValue placeholder="Warehouse…" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.action === "MOVE" && (
                <div className="col-span-6">
                  <Label>To warehouse</Label>
                  <Select value={form.to_warehouse_id} onValueChange={(v) => setForm({ ...form, to_warehouse_id: v })}>
                    <SelectTrigger data-testid="bc-to-wh"><SelectValue placeholder="Destination…" /></SelectTrigger>
                    <SelectContent>{warehouses.filter((w) => w.id !== form.warehouse_id).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>

        {lastResult && (
          <div className="rounded-md border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-900/20 p-3" data-testid="bc-last-result">
            <div className="text-xs label-overline text-emerald-700">Last scan</div>
            <div className="text-sm mt-1">
              <Badge className="mr-2">{lastResult.action}</Badge>
              Resolved as <span className="font-semibold">{lastResult.resolved.kind}</span>:{" "}
              <span className="font-mono">{lastResult.resolved.sku || lastResult.resolved.code || lastResult.resolved.number || lastResult.resolved.name || lastResult.resolved.id}</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display text-xl font-bold mb-2">Recent scans</h2>
        <div className="surface-card p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>At</TableHead><TableHead>Barcode</TableHead><TableHead>Action</TableHead>
              <TableHead>Resolved</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>By</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {scans.map((s) => (
                <TableRow key={s.id} data-testid={`bc-scan-row-${s.id}`}>
                  <TableCell className="font-mono text-xs">{(s.at || "").slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell className="font-mono text-xs">{s.barcode}</TableCell>
                  <TableCell><Badge variant="outline">{s.action}</Badge></TableCell>
                  <TableCell><Badge>{s.resolved_kind}</Badge></TableCell>
                  <TableCell className="text-right">{s.quantity}</TableCell>
                  <TableCell className="text-xs">{s.by}</TableCell>
                </TableRow>
              ))}
              {scans.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No scans yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
